import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { execMeetingAPI } from '../../api/client';
import { COLORS, RADIUS, btn, card, input as inputStyle } from '../../utils/uiStyles';
import RichEditor from './RichEditor';
import {
  DEPTS,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS_PER_REPORT,
  MAX_REPORT_ATTACHMENT_BYTES,
  RICH_FIELDS,
} from './execMeetingConst';

const TEXT_FIELDS = [
  'author',
  't1_title', 't1i1_label', 't1i2_label',
  't2_title', 't2i1_label', 't2i2_label',
  't3_title', 't3i1_label', 't3i2_label',
];

const THEMES = [
  { number: 1, title: 't1_title', items: [['t1i1_label', 't1i1'], ['t1i2_label', 't1i2']] },
  { number: 2, title: 't2_title', items: [['t2i1_label', 't2i1'], ['t2i2_label', 't2i2']] },
  { number: 3, title: 't3_title', items: [['t3i1_label', 't3i1'], ['t3i2_label', 't3i2']] },
];

function currentUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
}

// 부서 API 응답([{id, name, sort_order}]) → 이름 목록(순서 유지 · 빈 이름·중복 제거). 배열이 아니면 빈 목록.
//   UnifiedTab.jsx 에 같은 두 함수가 있다(한쪽을 고치면 같이 고친다).
function deptNames(data) {
  if (!Array.isArray(data)) return [];
  const seen = new Set();
  return data
    .map((item) => (typeof item === 'string' ? item : item?.name))
    .filter((name) => typeof name === 'string' && name.trim() && !seen.has(name) && seen.add(name));
}

// 부서 목록(#3130 · 관리자 탭에서 관리). 🔴 Promise 로 감싸 부른다 — departments 가 없는 가짜 API(옛 시험)에서도
//   동기 TypeError 로 화면이 죽지 않고 대체값(DEPTS)으로 떨어진다.
function loadDeptNames() {
  return Promise.resolve()
    .then(() => execMeetingAPI.departments())
    .then((response) => deptNames(response?.data));
}

function blankForm(author = '') {
  return {
    author,
    t1_title: '', t1i1_label: '', t1i2_label: '',
    t2_title: '', t2i1_label: '', t2i2_label: '',
    t3_title: '', t3i1_label: '', t3i2_label: '',
    kpis: [],
  };
}

function emptyRichValues() {
  return Object.fromEntries(RICH_FIELDS.map((field) => [field, '']));
}

function emptyEditorState(value = false) {
  return Object.fromEntries(RICH_FIELDS.map((field) => [field, value]));
}

function reportForm(report, fallbackAuthor) {
  const next = blankForm(fallbackAuthor);
  TEXT_FIELDS.forEach((field) => { next[field] = report?.[field] || ''; });
  next.kpis = Array.isArray(report?.kpis)
    ? report.kpis.slice(0, 100).map((item) => ({
      label: String(item?.label ?? ''),
      value: String(item?.value ?? ''),
      unit: String(item?.unit ?? ''),
    }))
    : [];
  return next;
}

function ownerMessage(owner) {
  const name = owner?.created_by_name || owner?.author || '다른 사용자';
  const email = owner?.created_by_email ? `(${owner.created_by_email})` : '';
  return `이미 ${name}${email} 님이 제출했습니다.`;
}

function errorMessage(error) {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return detail?.message || error.response?.data?.message || error.message || '요청을 처리하지 못했습니다.';
}

const labelStyle = { display: 'block', marginBottom: '6px', color: COLORS.textSub, fontSize: '12px', fontWeight: 700 };

function isNarrowSubmitForm() {
  return typeof window !== 'undefined' && window.innerWidth < 768;
}

// active = 제출 탭이 보이는지(ExecMeetingPage 는 쓰던 내용을 지키려고 이 탭을 늘 마운트한 채 숨긴다 · #3217).
export default function SubmitTab({ active = true } = {}) {
  const me = useMemo(currentUser, []);
  const [narrowSubmitForm, setNarrowSubmitForm] = useState(isNarrowSubmitForm);
  const [weeks, setWeeks] = useState([]);
  const [weekKey, setWeekKey] = useState('');
  const [dept, setDept] = useState(DEPTS[0]);
  const [deptOptions, setDeptOptions] = useState(DEPTS);
  const deptTouchedRef = useRef(false);
  const mountedRef = useRef(true);
  const prevActiveRef = useRef(active);
  const deptInitialPendingRef = useRef(true);
  const deptRefreshQueuedRef = useRef(false);
  const deptRefreshRequestRef = useRef(0);
  const [form, setForm] = useState(() => blankForm(me.name || ''));
  const [initialRich, setInitialRich] = useState(emptyRichValues);
  const [editorVersion, setEditorVersion] = useState(0);
  const [editorReady, setEditorReady] = useState(emptyEditorState);
  const [editorPending, setEditorPending] = useState(() => emptyEditorState(0));
  const [reportId, setReportId] = useState(null);
  // 첨부는 제출한 본인만(서버 can_attach · #3156) — reportId 를 바꾸는 자리에서 같이 바꾼다.
  const [canAttach, setCanAttach] = useState(true);
  const [files, setFiles] = useState([]);
  const [existingOwner, setExistingOwner] = useState(null);
  const [loadingWeeks, setLoadingWeeks] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [notice, setNotice] = useState(null);
  const editorRefs = useRef({});
  const contextRef = useRef({ generation: 0, weekKey: '', dept: DEPTS[0] });
  const saveRequestRef = useRef(0);

  const editorRefCallbacks = useMemo(
    () => Object.fromEntries(RICH_FIELDS.map((field) => [field, (node) => { editorRefs.current[field] = node; }])),
    [],
  );

  useEffect(() => {
    const updateLayout = () => setNarrowSubmitForm(isNarrowSubmitForm());
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const resetEditors = useCallback((values) => {
    setInitialRich(values || emptyRichValues());
    setEditorReady(emptyEditorState());
    setEditorPending(emptyEditorState(0));
    setEditorVersion((value) => value + 1);
  }, []);

  const beginContext = useCallback((nextWeek, nextDept) => {
    const context = {
      generation: contextRef.current.generation + 1,
      weekKey: nextWeek,
      dept: nextDept,
    };
    contextRef.current = context;
    saveRequestRef.current += 1;
    setWeekKey(nextWeek);
    setDept(nextDept);
    setReportId(null);
    setCanAttach(true);
    setFiles([]);
    setExistingOwner(null);
    setForm(blankForm(me.name || ''));
    resetEditors(emptyRichValues());
    setSaving(false);
    setUploadingFile(false);
    setLoadingReport(Boolean(nextWeek && nextDept));
    setNotice(null);
  }, [me.name, resetEditors]);

  useEffect(() => {
    let alive = true;
    setLoadingWeeks(true);
    execMeetingAPI.weeks()
      .then((response) => {
        if (!alive) return;
        const items = Array.isArray(response.data) ? response.data : [];
        setWeeks(items);
        const selected = items.find((item) => item.is_current) || items[0];
        if (selected) beginContext(selected.key, contextRef.current.dept);
      })
      .catch((error) => {
        if (alive) setNotice({ type: 'error', text: errorMessage(error) });
      })
      .finally(() => { if (alive) setLoadingWeeks(false); });
    return () => { alive = false; };
  }, [beginContext]);

  // 부서 선택지 다시 받기(#3217) — 선택지만 바꾼다. 🔴 폼·고른 부서(contextRef 포함)는 건드리지 않는다(쓰던 내용 유지).
  //   실패·빈 목록이면 옛 선택지 그대로. 다시 받기끼리는 뒤에 보낸 것만 반영하고, 언마운트 뒤 응답은 버린다.
  const refreshDeptOptions = useCallback(() => {
    const requestId = deptRefreshRequestRef.current + 1;
    deptRefreshRequestRef.current = requestId;
    loadDeptNames()
      .then((names) => {
        if (!mountedRef.current || deptRefreshRequestRef.current !== requestId || !names.length) return;
        setDeptOptions(names);
      })
      .catch(() => {});
  }, []);

  // 부서 선택지 = 관리자 탭의 부서 목록(사용 중인 것). 실패·빈 목록이면 DEPTS 그대로.
  //   기본 부서는 사용자가 아직 안 바꿨을 때만 목록 첫 항목으로(이미 고른 값을 덮지 않는다).
  //   첫 응답 처리가 끝나면(성공·실패 무관) 그 사이 미뤄 둔 다시 받기를 1회 부른다.
  useEffect(() => {
    let alive = true;
    deptInitialPendingRef.current = true;
    loadDeptNames()
      .then((names) => {
        if (!alive || !names.length) return;
        setDeptOptions(names);
        if (deptTouchedRef.current || contextRef.current.dept === names[0]) return;
        if (contextRef.current.weekKey) {
          beginContext(contextRef.current.weekKey, names[0]);
        } else {
          contextRef.current = { ...contextRef.current, dept: names[0] };
          setDept(names[0]);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!alive) return;
        deptInitialPendingRef.current = false;
        if (!deptRefreshQueuedRef.current) return;
        deptRefreshQueuedRef.current = false;
        refreshDeptOptions();
      });
    return () => { alive = false; };
  }, [beginContext, refreshDeptOptions]);

  // 숨겼다 다시 보일 때(active false → true · 첫 마운트 제외) 부서 선택지만 다시 받는다 — 관리자 탭에서 바꾼 부서가
  //   탭 전환만으로 반영된다. 첫 요청이 아직 안 끝났으면 표시만 해 둔다(늦은 첫 응답이 새 목록을 덮지 않게).
  useEffect(() => {
    const wasActive = prevActiveRef.current;
    prevActiveRef.current = active;
    if (!active || wasActive) return;
    if (deptInitialPendingRef.current) {
      deptRefreshQueuedRef.current = true;
      return;
    }
    refreshDeptOptions();
  }, [active, refreshDeptOptions]);

  // 지금 고른 부서가 목록에 없으면(사용 안 함으로 바뀐 부서의 옛 보고서 수정) 그 값도 선택지에 남긴다.
  const deptChoices = !dept || deptOptions.includes(dept) ? deptOptions : [...deptOptions, dept];

  useEffect(() => {
    if (!weekKey || !dept) return undefined;
    let cancelled = false;
    const snapshot = contextRef.current;
    const isCurrent = () => (
      !cancelled
      && contextRef.current.generation === snapshot.generation
      && contextRef.current.weekKey === snapshot.weekKey
      && contextRef.current.dept === snapshot.dept
    );

    (async () => {
      try {
        const listed = await execMeetingAPI.list(snapshot.weekKey);
        if (!isCurrent()) return;
        const reports = Array.isArray(listed.data) ? listed.data : [];
        const existing = reports.find((report) => report.dept === snapshot.dept);
        if (!existing) return;
        // 수정 가능 여부는 서버 판정(can_edit · 본인·admin·이관분 · #3156)만 본다 — 없으면(옛 응답) 막지 않고 PUT 이 판정한다.
        if (existing.can_edit === false) {
          setExistingOwner(existing);
          return;
        }
        const detailed = await execMeetingAPI.get(existing.id);
        if (!isCurrent()) return;
        const report = detailed.data || {};
        setReportId(report.id);
        setCanAttach(report.can_attach !== false);
        setFiles(Array.isArray(report.files) ? report.files : []);
        setForm(reportForm(report, me.name || ''));
        resetEditors(Object.fromEntries(RICH_FIELDS.map((field) => [field, report[field] || ''])));
      } catch (error) {
        if (isCurrent()) setNotice({ type: 'error', text: errorMessage(error) });
      } finally {
        if (isCurrent()) setLoadingReport(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dept, me.name, resetEditors, weekKey]);

  const handleEditorReady = useCallback((field, ready) => {
    setEditorReady((current) => (current[field] === ready ? current : { ...current, [field]: ready }));
  }, []);

  const handleEditorPending = useCallback((field, count) => {
    setEditorPending((current) => (current[field] === count ? current : { ...current, [field]: count }));
  }, []);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateKpi = (index, field, value) => setForm((current) => ({
    ...current,
    kpis: current.kpis.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
  }));
  const addKpi = () => setForm((current) => (
    current.kpis.length >= 100 ? current : { ...current, kpis: [...current.kpis, { label: '', value: '', unit: '' }] }
  ));
  const removeKpi = (index) => setForm((current) => ({
    ...current,
    kpis: current.kpis.filter((_, itemIndex) => itemIndex !== index),
  }));

  const allEditorsReady = RICH_FIELDS.every((field) => editorReady[field]);
  const hasPendingImages = RICH_FIELDS.some((field) => editorPending[field] > 0);
  const selectorsDisabled = saving || uploadingFile;

  const save = async (event) => {
    event.preventDefault();
    if (!weekKey || !dept || loadingReport || !allEditorsReady) {
      setNotice({ type: 'error', text: '본문을 모두 불러온 뒤 저장해 주세요.' });
      return;
    }
    const snapshot = { ...contextRef.current };
    const requestId = saveRequestRef.current + 1;
    saveRequestRef.current = requestId;
    const editingId = reportId;
    setSaving(true);
    setNotice(null);
    try {
      const htmlValues = await Promise.all(RICH_FIELDS.map(async (field) => {
        const editor = editorRefs.current[field];
        if (!editor) throw new Error('본문 편집기를 준비하지 못했습니다.');
        return [field, await editor.getHTML()];
      }));
      if (contextRef.current.generation !== snapshot.generation) return;
      const selectedWeek = weeks.find((week) => week.key === snapshot.weekKey);
      const payload = {
        week_key: snapshot.weekKey,
        week_label: selectedWeek?.label || '',
        dept: snapshot.dept,
        ...form,
        ...Object.fromEntries(htmlValues),
      };
      const response = editingId
        ? await execMeetingAPI.update(editingId, payload)
        : await execMeetingAPI.create(payload);
      if (contextRef.current.generation !== snapshot.generation || saveRequestRef.current !== requestId) return;
      const saved = response.data || {};
      if (saved.can_edit === false) {
        setReportId(null);
        setCanAttach(saved.can_attach !== false);
        setExistingOwner(saved);
        setNotice({ type: 'error', text: ownerMessage(saved) });
        return;
      }
      setReportId(saved.id || editingId);
      setCanAttach(saved.can_attach !== false);
      setExistingOwner(null);
      setFiles(Array.isArray(saved.files) ? saved.files : files);
      // 같은 그림이 두 번 들어 있으면 서버가 첫 장만 남기고 저장한다(#3142) — 화면(두 장)과 저장(한 장)이 어긋나지 않게
      //   편집기를 서버가 저장한 본문으로 다시 그린다.
      const dropped = Number(saved.dropped_duplicate_images) || 0;
      if (dropped > 0) {
        resetEditors(Object.fromEntries(RICH_FIELDS.map((field) => [field, saved[field] || ''])));
        setNotice({ type: 'success', text: `보고서를 저장했습니다. 같은 이미지가 두 번 들어 있어 ${dropped}장을 빼고 저장했습니다.` });
      } else {
        setNotice({ type: 'success', text: '보고서를 저장했습니다.' });
      }
    } catch (error) {
      if (contextRef.current.generation !== snapshot.generation || saveRequestRef.current !== requestId) return;
      if (error.response?.status === 409) {
        const detail = error.response?.data?.detail || error.response?.data || {};
        const { created_by_name, created_by_email, created_by, author } = detail;
        const owner = { created_by_name, created_by_email, created_by, author };
        setReportId(null);
        setCanAttach(true);
        setExistingOwner(owner);
        setNotice({ type: 'error', text: ownerMessage(owner) });
      } else {
        setNotice({ type: 'error', text: errorMessage(error) });
      }
    } finally {
      if (saveRequestRef.current === requestId) setSaving(false);
    }
  };

  const uploadAttachments = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (!reportId || !selectedFiles.length) return;
    if (files.length + selectedFiles.length > MAX_ATTACHMENTS_PER_REPORT) {
      setNotice({ type: 'error', text: '보고서당 첨부 파일은 20개까지 등록할 수 있습니다.' });
      return;
    }
    if (selectedFiles.some((file) => file.size > MAX_ATTACHMENT_BYTES)) {
      setNotice({ type: 'error', text: '첨부 파일은 개당 100MB까지 등록할 수 있습니다.' });
      return;
    }
    const existingBytes = files.reduce((total, file) => total + (Number(file?.bytes) || 0), 0);
    const selectedBytes = selectedFiles.reduce((total, file) => total + file.size, 0);
    if (existingBytes + selectedBytes > MAX_REPORT_ATTACHMENT_BYTES) {
      setNotice({ type: 'error', text: '보고서 첨부 파일의 합계는 100MB까지 등록할 수 있습니다.' });
      return;
    }
    const snapshot = { ...contextRef.current };
    const targetReportId = reportId;
    setUploadingFile(true);
    setNotice(null);
    try {
      const uploaded = [];
      for (const file of selectedFiles) {
        const upload = new FormData();
        upload.append('report_id', String(targetReportId));
        upload.append('file', file);
        const response = await execMeetingAPI.uploadFile(upload);
        uploaded.push(response.data);
      }
      if (contextRef.current.generation !== snapshot.generation || reportId !== targetReportId) return;
      setFiles((current) => [...current, ...uploaded]);
      setNotice({ type: 'success', text: '첨부 파일을 등록했습니다.' });
    } catch (error) {
      if (contextRef.current.generation === snapshot.generation) {
        setNotice({ type: 'error', text: errorMessage(error) });
      }
    } finally {
      setUploadingFile(false);
    }
  };

  return (
    <form onSubmit={save}>
      {notice && (
        <div role={notice.type === 'error' ? 'alert' : 'status'} style={{
          marginBottom: '14px', padding: '10px 12px', borderRadius: RADIUS.md, fontSize: '13px',
          color: notice.type === 'error' ? COLORS.dangerText : COLORS.successText,
          background: notice.type === 'error' ? COLORS.dangerSoft : COLORS.successSoft,
        }}>{notice.text}</div>
      )}
      {existingOwner && !reportId && (
        <div style={{ marginBottom: '14px', color: COLORS.warn, fontSize: '13px' }}>
          {existingOwner.created_by_name ? ownerMessage(existingOwner) : '이 주차와 부서는 다른 사용자가 먼저 제출한 상태입니다.'}
        </div>
      )}

      <section style={{ ...card, marginBottom: '14px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: narrowSubmitForm
              ? 'minmax(0, 1fr)'
              : 'minmax(180px, 1fr) minmax(180px, 1fr) minmax(180px, 1fr)',
            gap: '12px',
            ...(narrowSubmitForm ? { width: '100%', maxWidth: '100%', minWidth: 0 } : {}),
          }}
        >
          <label style={narrowSubmitForm ? { minWidth: 0 } : undefined}>
            <span style={labelStyle}>주차</span>
            <select aria-label="주차" value={weekKey} disabled={loadingWeeks || selectorsDisabled} onChange={(event) => beginContext(event.target.value, dept)} style={inputStyle}>
              {!weeks.length && <option value="">주차를 불러오는 중입니다</option>}
              {weeks.map((week) => <option key={week.key} value={week.key}>{week.label}</option>)}
            </select>
          </label>
          <label style={narrowSubmitForm ? { minWidth: 0 } : undefined}>
            <span style={labelStyle}>부서</span>
            <select aria-label="부서" value={dept} disabled={selectorsDisabled} onChange={(event) => { deptTouchedRef.current = true; beginContext(weekKey, event.target.value); }} style={inputStyle}>
              {deptChoices.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
          <label style={narrowSubmitForm ? { minWidth: 0 } : undefined}>
            <span style={labelStyle}>작성자</span>
            <input aria-label="작성자" value={form.author} disabled={loadingReport || saving} onChange={(event) => updateField('author', event.target.value)} style={inputStyle} />
          </label>
        </div>
      </section>

      {THEMES.map((theme) => (
        <section key={theme.number} style={{ ...card, marginBottom: '14px' }}>
          <h3 style={{ margin: '0 0 12px', color: COLORS.text, fontSize: '16px' }}>테마 {theme.number}</h3>
          <label>
            <span style={labelStyle}>테마 제목</span>
            <input value={form[theme.title]} disabled={loadingReport || saving} onChange={(event) => updateField(theme.title, event.target.value)} placeholder={`테마 ${theme.number} 제목`} style={{ ...inputStyle, marginBottom: '12px' }} />
          </label>
          {theme.items.map(([labelField, richField], index) => (
            <div key={richField} style={{ marginTop: index ? '14px' : 0 }}>
              <label>
                <span style={labelStyle}>항목 {index + 1} 라벨</span>
                <input value={form[labelField]} disabled={loadingReport || saving} onChange={(event) => updateField(labelField, event.target.value)} placeholder={`항목 ${index + 1}`} style={{ ...inputStyle, marginBottom: '8px' }} />
              </label>
              <RichEditor
                key={`${editorVersion}-${richField}`}
                ref={editorRefCallbacks[richField]}
                field={richField}
                initialHTML={initialRich[richField]}
                disabled={loadingReport || saving}
                onReady={handleEditorReady}
                onPendingChange={handleEditorPending}
              />
            </div>
          ))}
        </section>
      ))}

      {[['ai_usage', 'AI 활용'], ['etc', '기타']].map(([field, title]) => (
        <section key={field} style={{ ...card, marginBottom: '14px' }}>
          <h3 style={{ margin: '0 0 12px', color: COLORS.text, fontSize: '16px' }}>{title}</h3>
          <RichEditor
            key={`${editorVersion}-${field}`}
            ref={editorRefCallbacks[field]}
            field={field}
            initialHTML={initialRich[field]}
            disabled={loadingReport || saving}
            onReady={handleEditorReady}
            onPendingChange={handleEditorPending}
          />
        </section>
      ))}

      <section style={{ ...card, marginBottom: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, color: COLORS.text, fontSize: '16px' }}>KPI</h3>
          <button type="button" onClick={addKpi} disabled={form.kpis.length >= 100 || saving} style={btn('secondary', 'sm')}>행 추가</button>
        </div>
        {form.kpis.length === 0 && <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>등록된 KPI가 없습니다. (최대 100개)</div>}
        {form.kpis.map((kpi, index) => (
          <div key={index} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '8px', marginTop: '8px' }}>
            <input aria-label={`KPI ${index + 1} 이름`} value={kpi.label} disabled={saving} onChange={(event) => updateKpi(index, 'label', event.target.value)} placeholder="지표" style={inputStyle} />
            <input aria-label={`KPI ${index + 1} 값`} value={kpi.value} disabled={saving} onChange={(event) => updateKpi(index, 'value', event.target.value)} placeholder="값" style={inputStyle} />
            <input aria-label={`KPI ${index + 1} 단위`} value={kpi.unit} disabled={saving} onChange={(event) => updateKpi(index, 'unit', event.target.value)} placeholder="단위" style={inputStyle} />
            <button type="button" onClick={() => removeKpi(index)} disabled={saving} style={btn('danger', 'sm')}>삭제</button>
          </div>
        ))}
      </section>

      <section style={{ ...card, marginBottom: '14px' }}>
        <h3 style={{ margin: '0 0 8px', color: COLORS.text, fontSize: '16px' }}>첨부</h3>
        <div style={{ marginBottom: '8px', color: COLORS.textSub, fontSize: '12px' }}>개당 100MB · 보고서당 20개 · 합계 100MB</div>
        <input aria-label="첨부 파일" type="file" multiple disabled={!reportId || !canAttach || uploadingFile || saving} onChange={uploadAttachments} />
        {!reportId && <div style={{ marginTop: '8px', color: COLORS.warn, fontSize: '13px' }}>저장하면 첨부를 붙일 수 있습니다.</div>}
        {reportId && canAttach === false && <div style={{ marginTop: '8px', color: COLORS.warn, fontSize: '13px' }}>첨부는 제출한 본인만 올릴 수 있습니다.</div>}
        {files.length > 0 && <ul style={{ margin: '10px 0 0', paddingLeft: '20px', color: COLORS.text, fontSize: '13px' }}>{files.map((file) => <li key={file.id || file.name}>{file.name}</li>)}</ul>}
      </section>

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
        {hasPendingImages && <span role="status" style={{ color: COLORS.textSub, fontSize: '12px' }}>이미지를 업로드하는 중입니다.</span>}
        <button type="submit" disabled={loadingWeeks || loadingReport || saving || !allEditorsReady || hasPendingImages || !weekKey} style={{ ...btn('primary'), opacity: loadingReport || saving || !allEditorsReady || hasPendingImages ? 0.55 : 1 }}>
          {saving ? '저장 중…' : reportId ? '수정 저장' : '저장'}
        </button>
      </div>
    </form>
  );
}
